import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Compass, RotateCcw, Loader2, AlertCircle, Navigation, Save, Share2, Trash2, FolderOpen, Check, ChevronRight, Landmark, Settings, Sparkles, X, Lock, CloudRain, Sun, CloudSun, Map as MapIcon } from 'lucide-react';
import { cn } from '../ui/cn';
import type { UseTripCopilotState } from '../../hooks/useTripCopilot';
import type { RouteAttraction } from '../../services/tripPlannerService';
import type { CampingSpot } from '../../types';
import { COPILOT_GA } from '../../services/accessControl';

interface CopilotPanelProps {
  copilot: UseTripCopilotState;
  onStartNavigation: () => void;
  /** «Vis på kartet»: minimer chatten så kartet (med tegnet rute + markører) kommer frem.
   *  Når satt blir denne PRIMÆR handling i planlagt-fasen; navigasjon demoteres til ikon. */
  onShowOnMap?: () => void;
  /** Total lengde/tid for tegnet rute (fra App). */
  routeStats?: { km: number; durationText: string } | null;
  /** Utforsk et overnattingsstopp — fly til det + åpne kortet. */
  onExploreStop?: (spot: CampingSpot) => void;
  /** Utforsk en attraksjon langs ruten — fly til den. */
  onExploreAttraction?: (a: RouteAttraction) => void;
}

// Klype-zoom i chatten (mobil): skalerer innholdet via CSS zoom. Klampet + persistert.
const CHAT_SCALE_KEY = 'fricamp_copilot_chat_scale';
export const clampChatScale = (v: number): number => (Number.isFinite(v) ? Math.min(1.6, Math.max(0.8, v)) : 1);
const loadChatScale = (): number => {
  try { return clampChatScale(parseFloat(localStorage.getItem(CHAT_SCALE_KEY) || '1')); } catch { return 1; }
};
const touchDist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

// MET sine offisielle værsymboler (samme kilde som WeatherBar). symbol_code → SVG-URL.
const metIconUrl = (code: string) => `https://raw.githubusercontent.com/metno/weathericons/main/weather/svg/${code}.svg`;
// Lokaliserte ukedag/dato via Intl (følger brukerens språk).
const weekdayShort = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(`${iso}T12:00:00`));
const dateLabel = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${iso}T12:00:00`));

/** AI kan returnere sentinel-nøkler (f.eks. current_location) som chip-tekst.
 *  Vis lokaliserte labels; send fortsatt råverdien så GPS-resolving fungerer. */
export function formatChoiceLabel(raw: string, t: (key: string, fallback?: string) => string): string {
  const normalized = raw.trim().toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'current_location') return t('copilot.current_location', 'Min posisjon');
  return raw;
}

/** Eksklusiv overlay i CopilotPanel: settings XOR trips (ISSUE-005). */
export function toggleExclusiveOverlay(
  current: 'none' | 'settings' | 'trips',
  target: 'settings' | 'trips',
): 'none' | 'settings' | 'trips' {
  return current === target ? 'none' : target;
}

/** Svar-chip: normal case (uppercase er reservert til labels i DESIGN.md), 44px touch-mål. */
function ChoiceChip({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'min-h-[44px] shrink-0 whitespace-nowrap rounded-pill border border-brand/40 bg-brand/10 px-4 text-sm font-medium text-brand',
        'transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
        'hover:bg-brand/20 disabled:opacity-50',
      )}
    >
      {label}
    </button>
  );
}

/**
 * Reise-copilot — sidepanel-innhold. Rendres INNE i venstre SidebarPanel (som spots/ruter),
 * så kartet forblir synlig. Fyller containeren (flex-col, h-full). Ruten tegnes på kartet
 * via App.state.routePlan (copiloten setter kun spec'en; App tegner).
 */
const CopilotPanel: React.FC<CopilotPanelProps> = ({ copilot, onStartNavigation, onShowOnMap, routeStats, onExploreStop, onExploreAttraction }) => {
  const {
    phase, messages, tripSpec, suggestedChoices, thinking, planning, statusMsg, error,
    planResult, savedTrips, saving, sendMessage, confirmPlan, reset, retry,
    saveCurrentTrip, loadTrip, deleteSavedTrip, shareCurrentTrip,
    learningEnabled, learnedFacts, setLearningEnabled, clearLearnedMemory,
    weather, suggestBetterWeather,
  } = copilot;

  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'no';
  const [draft, setDraft] = useState('');
  const [overlay, setOverlay] = useState<'none' | 'settings' | 'trips'>('none');
  const showTrips = overlay === 'trips';
  const showSettings = overlay === 'settings';
  const [shareMsg, setShareMsg] = useState('');
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Klype-zoom (mobil): to fingre på meldingsområdet skalerer teksten (0.8–1.6, husket).
  // Native touchmove-listener m/ passive:false — Reacts syntetiske er passiv og kan ikke
  // preventDefault (som trengs for å hindre at nettleseren zoomer hele siden).
  const [chatScale, setChatScale] = useState(loadChatScale);
  const chatScaleRef = useRef(chatScale); chatScaleRef.current = chatScale;
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) pinchRef.current = { dist: touchDist(e.touches), scale: chatScaleRef.current };
    };
    const onMove = (e: TouchEvent) => {
      const p = pinchRef.current;
      if (!p || e.touches.length !== 2) return;
      e.preventDefault();
      setChatScale(clampChatScale(p.scale * (touchDist(e.touches) / p.dist)));
    };
    const onEnd = () => {
      if (!pinchRef.current) return;
      pinchRef.current = null;
      try { localStorage.setItem(CHAT_SCALE_KEY, String(chatScaleRef.current)); } catch { /* stille */ }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const handleShare = async () => {
    const url = await shareCurrentTrip();
    if (!url) { setShareMsg(t('copilot.actions.share_failed')); return; }
    try { await navigator.clipboard.writeText(url); setShareMsg(t('copilot.actions.share_copied')); }
    catch { setShareMsg(url); }
    setTimeout(() => setShareMsg(''), 3000);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, planning]);

  const submit = () => {
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    sendMessage(t);
  };

  return (
    <div className="flex h-full flex-col bg-[var(--bg-surface)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
        <div className="flex items-center gap-2">
          <Compass className="h-5 w-5 text-brand" />
          <span className="font-semibold text-[var(--text-primary)]">{t('copilot.title')}</span>
          {!COPILOT_GA && (
            <span className="rounded-pill bg-brand/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-brand">{t('copilot.badge_test')}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {savedTrips.length > 0 && (
            <button
              type="button"
              onClick={() => setOverlay((o) => toggleExclusiveOverlay(o, 'trips'))}
              aria-label={t('copilot.trips.heading')}
              className={cn('grid h-11 w-11 place-items-center hover:text-[var(--text-secondary)]', showTrips ? 'text-brand' : 'text-[var(--text-muted)]')}
            >
              <FolderOpen className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOverlay((o) => toggleExclusiveOverlay(o, 'settings'))}
            aria-label={t('copilot.aria.settings')}
            className={cn('grid h-11 w-11 place-items-center hover:text-[var(--text-secondary)]', showSettings ? 'text-brand' : 'text-[var(--text-muted)]')}
          >
            <Settings className="h-4 w-4" />
          </button>
          {messages.length > 0 && (
            <button
              type="button" onClick={reset} aria-label={t('copilot.aria.restart')}
              className="grid h-11 w-11 place-items-center text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Innstillinger: «lær om deg» */}
      {showSettings && (
        <div className="border-b border-[var(--border-primary)] bg-[var(--bg-surface-alt)] px-4 py-2.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <p className="text-sm font-semibold text-[var(--text-primary)]">{t('copilot.learn.title')}</p>
                <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                  {t('copilot.learn.desc')}
                  <span className="ml-1 inline-flex items-center gap-1 text-brand">
                    <Lock className="h-3 w-3 shrink-0" /> {t('copilot.learn.privacy_short')}
                  </span>
                </p>
              </div>
            </div>
            {/* Bryter */}
            <button
              type="button" role="switch" aria-checked={learningEnabled}
              onClick={() => setLearningEnabled(!learningEnabled)}
              aria-label={t('copilot.learn.toggle_aria')}
              className={cn(
                'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors',
                learningEnabled ? 'bg-brand' : 'bg-[var(--border-primary)]',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform',
                  learningEnabled ? 'translate-x-[22px]' : 'translate-x-0.5',
                )}
              />
            </button>
          </div>

          {learnedFacts.length > 0 ? (
            <div className="mt-3">
              <p className="mb-1.5 text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)]">{t('copilot.learn.learned_count', { count: learnedFacts.length })}</p>
              <div className="flex flex-wrap gap-1.5">
                {learnedFacts.map((f, i) => (
                  <span key={`${f}-${i}`} className="rounded-pill bg-[var(--bg-input)] px-2.5 py-1 text-xs text-[var(--text-secondary)]">{f}</span>
                ))}
              </div>
              <button
                type="button" onClick={clearLearnedMemory}
                className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-400 hover:text-red-300"
              >
                <X className="h-3.5 w-3.5" /> {t('copilot.learn.clear')}
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-[var(--text-muted)]">
              {learningEnabled ? t('copilot.learn.empty_on') : t('copilot.learn.empty_off')}
            </p>
          )}
        </div>
      )}

      {/* Meldingsliste + innhold. touch-action pan-y: vertikal scroll beholdes, men
          nettleserens egen pinch tas ikke — vår klype-zoom (CSS zoom på indre wrapper)
          skalerer chat-innholdet i stedet. */}
      <div
        ref={scrollRef}
        aria-live="polite"
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ touchAction: 'pan-y' }}
      >
       <div className="space-y-3" style={{ zoom: chatScale } as React.CSSProperties}>
        {showTrips && (
          <div className="rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] p-2">
            <p className="px-1 py-1 text-xs font-black uppercase tracking-widest text-[var(--text-muted)]">{t('copilot.trips.heading')}</p>
            {savedTrips.length === 0 && <p className="px-1 pb-1 text-sm text-[var(--text-muted)]">{t('copilot.trips.empty')}</p>}
            {savedTrips.map((tr) => (
              <div key={tr.id} className="flex items-center gap-2 rounded-xl px-1 py-1.5 hover:bg-[var(--bg-input)]">
                <button
                  type="button" onClick={() => { loadTrip(tr); setOverlay('none'); }}
                  className="min-w-0 flex-1 truncate text-left text-sm text-[var(--text-primary)]"
                >
                  {tr.title}
                </button>
                <button
                  type="button" onClick={() => deleteSavedTrip(tr.id)} aria-label={t('copilot.trips.delete_aria')}
                  className="grid h-9 w-9 shrink-0 place-items-center text-[var(--text-muted)] hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tomtilstand (H1): vises UMIDDELBART når panelet åpnes — før AI-hilsenen lander
            og hvis den feiler. Gir en ny bruker verdiforslag + eksempler å trykke på, i
            stedet for et blankt panel med pulsprikker. Skjules så snart første melding kommer. */}
        {messages.length === 0 && !showTrips && (
          <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-brand/15">
              <Compass className="h-6 w-6 text-brand" />
            </span>
            <div>
              <p className="text-base font-semibold text-[var(--text-primary)]">{t('copilot.welcome.title')}</p>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{t('copilot.welcome.subtitle')}</p>
            </div>
            <div className="mt-1 flex flex-wrap justify-center gap-2">
              {[t('copilot.welcome.ex1'), t('copilot.welcome.ex2'), t('copilot.welcome.ex3')].map((ex) => (
                <ChoiceChip key={ex} label={ex} disabled={thinking} onClick={() => sendMessage(ex)} />
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={`${m.ts}-${i}`} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
                m.role === 'user' ? 'bg-brand text-stone-900' : 'bg-[var(--bg-surface-alt)] text-[var(--text-primary)]',
              )}
            >
              {m.content}
            </div>
          </div>
        ))}

        {thinking && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1 rounded-2xl bg-[var(--bg-surface-alt)] px-3 py-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-brand [animation-delay:0ms]" />
              <span className="h-2 w-2 animate-pulse rounded-full bg-brand [animation-delay:150ms]" />
              <span className="h-2 w-2 animate-pulse rounded-full bg-brand [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {planning && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{statusMsg || t('copilot.status.planning')}</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
            {/* Retry (H3): uten dette ble en ny bruker stående fast i en tom chat når hilsenen feilet. */}
            <button
              type="button" onClick={retry} disabled={thinking}
              className="flex min-h-[44px] items-center gap-1.5 self-start rounded-pill border border-red-500/40 px-4 text-sm font-medium text-red-300 transition active:scale-95 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" /> {t('copilot.error.retry')}
            </button>
          </div>
        )}

        {planResult && planResult.dayPlan.length > 0 && (
          <div className="rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] p-3">
            <p className="mb-2 text-xs font-black uppercase tracking-widest text-[var(--text-muted)]">
              {t('copilot.plan.heading', { count: planResult.dayPlan.length })}
              {routeStats ? ` · ${routeStats.km} km · ${routeStats.durationText}` : ''}
            </p>
            <ol className="space-y-1">
              {planResult.dayPlan.map((leg) => {
                // «to» er et overnattingsstopp når day ≤ antall stopp (siste etappe går til målet).
                const stop = leg.day <= planResult.stops.length ? planResult.stops[leg.day - 1] : null;
                const tappable = !!(stop && onExploreStop);
                const Row = tappable ? 'button' : 'div';
                return (
                  <Row
                    key={leg.day}
                    {...(tappable ? { type: 'button', onClick: () => onExploreStop!(stop!) } : {})}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-xl px-1 py-1.5 text-left text-sm text-[var(--text-primary)]',
                      tappable && 'hover:bg-[var(--bg-input)]',
                    )}
                  >
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand/15 text-[10px] font-black text-brand">{leg.day}</span>
                    <span className="min-w-0 flex-1 truncate">{leg.from} → {leg.to}</span>
                    <span className="shrink-0 text-xs text-[var(--text-muted)]">~{leg.km} km</span>
                    {tappable && <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-muted)]" />}
                  </Row>
                );
              })}
            </ol>
            {planResult.stops.length > 0 && onExploreStop && (
              <p className="mt-2 px-1 text-[10px] text-[var(--text-muted)]">{t('copilot.plan.tap_hint')}</p>
            )}
          </div>
        )}

        {weather && weather.verdict !== 'unknown' && (
          (() => {
            const bad = weather.verdict === 'bad';
            const good = weather.verdict === 'good';
            const Icon = good ? Sun : bad ? CloudRain : CloudSun;
            const tone = good ? 'border-brand/30 bg-brand/10' : bad ? 'border-amber-500/40 bg-amber-500/10' : 'border-[var(--border-primary)] bg-[var(--bg-surface-alt)]';
            const accent = good ? 'text-brand' : bad ? 'text-amber-500' : 'text-[var(--text-secondary)]';
            const dailies = weather.dailies || [];
            const wHeadline = t(bad ? 'copilot.weather.bad' : good ? 'copilot.weather.good' : 'copilot.weather.mixed',
              { rainy: weather.rainyDays, days: weather.assessedDays, temp: weather.avgHighC });
            return (
              <div className={cn('overflow-hidden rounded-2xl border', tone)}>
                {/* Kompakt topprad — trykk for langtidsvarsel */}
                <button
                  type="button" onClick={() => setWeatherExpanded((v) => !v)}
                  aria-expanded={weatherExpanded} disabled={dailies.length === 0}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
                >
                  <Icon className={cn('h-5 w-5 shrink-0', accent)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[var(--text-primary)]">{t('copilot.weather.title', { place: weather.place.split(',')[0] })}</span>
                    <span className="block truncate text-xs text-[var(--text-secondary)]">{wHeadline}</span>
                  </span>
                  {dailies.length > 0 && (
                    <ChevronRight className={cn('h-4 w-4 shrink-0 text-[var(--text-muted)] transition-transform', weatherExpanded && 'rotate-90')} />
                  )}
                </button>

                {/* Kompakt symbol-stripe (dominerer ikke) vs utvidet langtidsvarsel */}
                {dailies.length > 0 && !weatherExpanded && (
                  <div className="flex gap-1 overflow-x-auto px-3 pb-2.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {dailies.slice(0, 7).map((d) => (
                      <div key={d.date} className="flex min-w-[42px] shrink-0 flex-col items-center gap-0.5">
                        <span className="text-[10px] text-[var(--text-muted)]">{weekdayShort(d.date, locale)}</span>
                        <img src={metIconUrl(d.symbol)} alt="" loading="lazy" className="h-6 w-6" />
                        <span className="text-xs font-bold text-[var(--text-primary)]">{d.tempMax}°</span>
                      </div>
                    ))}
                  </div>
                )}
                {dailies.length > 0 && weatherExpanded && (
                  <div className="space-y-0.5 px-2 pb-2">
                    {dailies.map((d) => (
                      <div key={d.date} className="flex items-center gap-2 rounded-xl px-2 py-1.5">
                        <img src={metIconUrl(d.symbol)} alt="" loading="lazy" className="h-8 w-8 shrink-0" />
                        <span className="w-24 shrink-0 text-sm text-[var(--text-primary)]">{dateLabel(d.date, locale)}</span>
                        <span className="flex-1 text-xs text-[var(--text-muted)]">{d.precipMm > 0 ? t('copilot.weather.mm', { mm: d.precipMm }) : t('copilot.weather.dry')}</span>
                        <span className="text-sm font-bold text-[var(--text-primary)]">{d.tempMax}°</span>
                        <span className="w-7 text-right text-xs text-[var(--text-muted)]">{d.tempMin}°</span>
                      </div>
                    ))}
                  </div>
                )}

                {bad && (
                  <div className="flex flex-col gap-2 border-t border-amber-500/20 px-3 py-2.5">
                    <p className="text-xs text-[var(--text-primary)]">{t('copilot.weather.sure')}</p>
                    <button
                      type="button" onClick={suggestBetterWeather} disabled={thinking}
                      className="min-h-[40px] self-start rounded-pill border border-brand/40 bg-brand/10 px-4 text-sm font-medium text-brand disabled:opacity-50"
                    >
                      {t('copilot.weather.find_better')}
                    </button>
                  </div>
                )}
              </div>
            );
          })()
        )}

        {planResult && planResult.attractions.length > 0 && (
          <div className="rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[var(--text-muted)]">
              <Landmark className="h-3.5 w-3.5" /> {t('copilot.attractions.heading', { count: planResult.attractions.length })}
            </p>
            <ul className="space-y-1">
              {planResult.attractions.map((a, i) => (
                <li key={`${a.name}-${i}`}>
                  <button
                    type="button"
                    onClick={() => onExploreAttraction?.(a)}
                    disabled={!onExploreAttraction}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-xl px-1 py-1.5 text-left text-sm text-[var(--text-primary)]',
                      onExploreAttraction && 'hover:bg-[var(--bg-input)]',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{a.name}</span>
                      {a.matchedInterests && a.matchedInterests.length > 0 && (
                        <span className="mt-0.5 block truncate text-xs text-brand">{t('copilot.attractions.matched', { list: a.matchedInterests.join(', ') })}</span>
                      )}
                    </span>
                    {onExploreAttraction && <ChevronRight className="h-4 w-4 shrink-0 text-[var(--text-muted)]" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {planResult?.hitMaxStops && (
          <p className="text-xs text-[var(--text-muted)]">{t('copilot.notice.max_stops')}</p>
        )}
        {planResult?.emptyCorridor && (
          <p className="text-xs text-[var(--text-muted)]">{t('copilot.notice.empty_corridor')}</p>
        )}
       </div>{/* zoom-wrapper */}
      </div>

      {/* Chips — bryter til flere rader så ingen kuttes; taket hindrer at de spiser
          hele høyden (scroller vertikalt hvis det er svært mange valg). */}
      {suggestedChoices.length > 0 && !thinking && (
        <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto px-4 py-2">
          {suggestedChoices.map((c) => (
            <ChoiceChip key={c} label={formatChoiceLabel(c, t)} disabled={thinking} onClick={() => sendMessage(c)} />
          ))}
        </div>
      )}

      {/* Forslag-fase: bekreft-gate */}
      {phase === 'proposal' && tripSpec.status === 'ready' && !planning && (
        <div className="px-4 py-2">
          <button
            type="button" onClick={confirmPlan}
            className="min-h-[48px] w-full rounded-pill bg-brand text-base font-semibold text-stone-900 transition active:scale-[0.98]"
          >
            {t('copilot.confirm.draw')}
          </button>
        </div>
      )}

      {/* Planlagt-fase: UTFORSKNING er primærhandling — «Vis på kartet» minimerer chatten
          så ruten + markørene kommer frem. Navigasjon er bevisst demotert til ikon-knapp
          (det letteste trykket skal aldri starte GPS-sporing). Uten onShowOnMap (eldre
          bruk): navigasjon forblir primær. */}
      {phase === 'planned' && planResult && !planning && (
        <div className="space-y-2 px-4 py-2">
          {/* Primærhandling — full bredde. «Vis på kartet» (utforsk) er bevisst primær;
              navigasjon demoteres til sekundær-raden (letteste trykk skal ikke starte GPS).
              Uten onShowOnMap (eldre bruk) er navigasjon primær. */}
          <button
            type="button" onClick={onShowOnMap ?? onStartNavigation}
            className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-pill bg-brand text-base font-semibold text-stone-900 transition active:scale-[0.98]"
          >
            {onShowOnMap
              ? <><MapIcon className="h-5 w-5" /> {t('copilot.actions.show_on_map')}</>
              : <><Navigation className="h-5 w-5" /> {t('copilot.actions.navigate')}</>}
          </button>
          {/* Sekundære handlinger MED SYNLIGE ETIKETTER (H5): ikon-only knapper var ikke
              oppdagbare for en ny bruker («hva gjør denne sirkelen?»). Nå ikon + tekst. */}
          <div className="flex items-center gap-2">
            {onShowOnMap && (
              <button
                type="button" onClick={onStartNavigation}
                className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-pill border border-[var(--border-primary)] px-2 text-sm font-medium text-[var(--text-secondary)] transition active:scale-95"
              >
                <Navigation className="h-4 w-4 shrink-0" /> <span className="truncate">{t('copilot.actions.navigate_short')}</span>
              </button>
            )}
            <button
              type="button" onClick={saveCurrentTrip} disabled={saving}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-pill border border-[var(--border-primary)] px-2 text-sm font-medium text-[var(--text-secondary)] transition active:scale-95 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Save className="h-4 w-4 shrink-0" />} <span className="truncate">{t('copilot.actions.save_short')}</span>
            </button>
            <button
              type="button" onClick={handleShare}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-pill border border-[var(--border-primary)] px-2 text-sm font-medium text-[var(--text-secondary)] transition active:scale-95"
            >
              {shareMsg ? <Check className="h-4 w-4 shrink-0 text-brand" /> : <Share2 className="h-4 w-4 shrink-0" />} <span className="truncate">{t('copilot.actions.share_short')}</span>
            </button>
          </div>
          {shareMsg && <p className="text-center text-xs text-brand">{shareMsg}</p>}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 border-t border-[var(--border-primary)] px-3 py-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={phase === 'planned' ? t('copilot.input.placeholder_planned') : t('copilot.input.placeholder_default')}
          disabled={thinking}
          className={cn(
            'min-h-[44px] flex-1 rounded-pill bg-[var(--bg-input)] px-4 text-sm text-[var(--text-primary)]',
            'placeholder:text-[var(--text-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
          )}
        />
        <button
          type="button" onClick={submit} disabled={thinking || !draft.trim()} aria-label={t('copilot.input.send_aria')}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand text-stone-900 disabled:opacity-40"
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default CopilotPanel;
